/**
 * Bridge the shell's `EY_RAPToR_*` window globals (and key shell events) into
 * same-origin MFE iframes.
 *
 * WHY: iframe-mounted legacy apps (e.g. USIR) read ALL their shell context —
 * clientId, the access token, user identity, side-nav state — from BARE window
 * globals like `EY_RAPToR_getCurrentClientId()` / `EY_RAPToR_getAccessTokenforMFE()`.
 * The deployed bundle looks them up on its OWN `window` (`He = window`), so
 * inside an iframe (a separate window) they're absent: `getCurrentId()` returns
 * `null` and the token helper throws "Shell function not available", leaving the
 * app unauthenticated.
 *
 * The mfe-loader iframe loader tags each iframe `data-mfe-loader-iframe`. This
 * module watches for those iframes and, on every `load`, copies the parent's
 * `EY_RAPToR_*` function globals onto the iframe's `contentWindow`. Copying the
 * function *reference* is enough: a function keeps its original closure no
 * matter which window holds it, so calling
 * `iframe.contentWindow.EY_RAPToR_getCurrentClientId()` runs the shell's real
 * implementation and returns the shell's current clientId / token.
 *
 * It also forwards the shell's `EY.RAPToR.*` window events into the iframe so
 * the embedded app reacts to client changes / side-nav toggles like an
 * inline-mounted MFE.
 *
 * Same-origin only — in dev the Vite `/static` proxy and in prod the shared
 * portal/CDN origin both keep the iframe same-origin, so `contentWindow` is
 * accessible. (Cross-origin iframes would be unreachable and are not used.)
 *
 * NOTE: this restores the runtime context an iframe app needs, but it cannot
 * bypass the app's own hardcoded cross-origin backend CORS policy — in local
 * dev those API calls are still blocked from `localhost` (same limitation as
 * MSAL / the MCP chat). On a deployed origin both the globals and the backend
 * work, giving full parity with the legacy inline mount.
 */

const IFRAME_SELECTOR = 'iframe[data-mfe-loader-iframe]';

// Globals the shell exposes for legacy MFEs. Everything `EY_RAPToR_*` plus the
// few non-prefixed aliases `install-globals` also publishes.
const GLOBAL_PREFIX_RE = /^EY_RAPToR_/;
const EXTRA_GLOBAL_KEYS = ['getAccessTokenforMFE', 'routeToMFE'];

// Shell → iframe events. Legacy MFEs subscribe to these on their own window;
// the shell dispatches them on the top window, so re-dispatch into the iframe.
const FORWARDED_EVENTS = [
  'EY.RAPToR.Client.Change',
  'EY.RAPToR.App.sidenavstatus',
  'EY.RAPToR.App.Loader.Running',
];

const attached = new WeakSet<HTMLIFrameElement>();

function bridgeGlobalsInto(win: Window): void {
  const parent = window as unknown as Record<string, unknown>;
  const child = win as unknown as Record<string, unknown>;
  for (const key of Object.keys(parent)) {
    if (GLOBAL_PREFIX_RE.test(key) || EXTRA_GLOBAL_KEYS.includes(key)) {
      try {
        child[key] = parent[key];
      } catch {
        /* property may be non-writable on the child window — skip */
      }
    }
  }
}

function forwardEventsInto(win: Window): () => void {
  const registered: Array<[string, EventListener]> = [];
  for (const type of FORWARDED_EVENTS) {
    const handler: EventListener = (event) => {
      try {
        win.dispatchEvent(new CustomEvent(type, { detail: (event as CustomEvent).detail }));
      } catch {
        /* iframe window torn down — handler will be cleaned up on next load */
      }
    };
    window.addEventListener(type, handler);
    registered.push([type, handler]);
  }
  return () => {
    for (const [type, handler] of registered) window.removeEventListener(type, handler);
  };
}

function attach(iframe: HTMLIFrameElement): void {
  if (attached.has(iframe)) return;
  attached.add(iframe);

  let cleanupForward: (() => void) | null = null;

  const inject = (): void => {
    const win = iframe.contentWindow;
    if (!win) return;
    bridgeGlobalsInto(win);
    // Re-establish event forwarding for the (possibly new) window.
    cleanupForward?.();
    cleanupForward = forwardEventsInto(win);
    win.addEventListener(
      'unload',
      () => {
        cleanupForward?.();
        cleanupForward = null;
      },
      { once: true },
    );
  };

  // Re-inject on every navigation/load — a client switch can reload the iframe,
  // and each fresh document gets a fresh window that needs the globals again.
  iframe.addEventListener('load', inject);

  // If the iframe already finished loading before we attached (race), inject now.
  try {
    if (iframe.contentWindow && iframe.contentDocument?.readyState === 'complete') {
      inject();
    }
  } catch {
    /* contentDocument not accessible yet — the load listener will handle it */
  }
}

let observer: MutationObserver | null = null;

/**
 * Start bridging shell globals into MFE iframes. Idempotent. Call once at
 * bootstrap AFTER `installLegacyGlobals()` so the `EY_RAPToR_*` globals exist.
 */
export function installIframeGlobalsBridge(): void {
  if (observer || typeof document === 'undefined') return;

  // Bridge any iframes already in the DOM.
  document.querySelectorAll<HTMLIFrameElement>(IFRAME_SELECTOR).forEach(attach);

  observer = new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      mutation.addedNodes.forEach((node) => {
        if (!(node instanceof HTMLElement)) return;
        if (node instanceof HTMLIFrameElement && node.matches(IFRAME_SELECTOR)) {
          attach(node);
        } else {
          node.querySelectorAll?.<HTMLIFrameElement>(IFRAME_SELECTOR).forEach(attach);
        }
      });
    }
  });
  observer.observe(document.documentElement, { childList: true, subtree: true });
}
